import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { getVideosFor } from './intentToVideos';
import { assertKnownTopic } from '../../constants/aiSearchTopics';
import VideoPlayerModal from '../shared/VideoPlayerModal';
import { trackVideoClick } from '../../utils/trackVideoClick';

const VideoCard = ({ video, onPlay }) => {
  return (
    <button
      type="button"
      onClick={() => onPlay(video)}
      className="flex-shrink-0 w-48 text-left bg-white rounded-xl border border-gray-200 overflow-hidden snap-start cursor-pointer hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors"
      aria-label={`Play ${video.title}`}
    >
      <div className="relative aspect-video">
        <img
          src={video.thumbnailUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
            <Play
              className="w-5 h-5 text-gray-900 ml-0.5"
              aria-hidden="true"
            />
          </div>
        </div>
        {video.duration ? (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
            {video.duration}
          </div>
        ) : null}
      </div>

      <div className="p-3">
        <h4 className="text-xs font-medium text-gray-900 line-clamp-2">
          {video.title}
        </h4>
        {video.source ? (
          <p className="text-xs text-gray-400 mt-1">{video.source}</p>
        ) : null}
      </div>
    </button>
  );
};

const VideoCarousel = ({ intent, topic }) => {
  const { t } = useTranslation();
  assertKnownTopic(topic);
  const videos = getVideosFor({ intent, topic });
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const update = () => {
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [videos.length]);

  if (videos.length === 0) return null;

  const scrollByDirection = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.8, 200);
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  const hasOverflow = canScrollLeft || canScrollRight;

  const handlePlay = (video) => {
    trackVideoClick({ title: video.title, carousel: 'related' });
    setActiveVideo(video);
  };

  return (
    <section aria-label={t('related_videos', 'Related videos')}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
          {t('related_videos_heading', 'Related Videos')}
        </h3>
        {hasOverflow ? (
          <div className="hidden max-[705px]:flex items-center gap-1">
            <button
              type="button"
              onClick={() => scrollByDirection(-1)}
              disabled={!canScrollLeft}
              aria-label={t('engagement_carousel.scroll_prev', 'Scroll videos left')}
              className="w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-700 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-opacity"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => scrollByDirection(1)}
              disabled={!canScrollRight}
              aria-label={t('engagement_carousel.scroll_next', 'Scroll videos right')}
              className="w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-700 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-opacity"
            >
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide snap-x snap-proximity scroll-smooth"
      >
        {videos.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            onPlay={handlePlay}
          />
        ))}
      </div>

      {activeVideo ? (
        <VideoPlayerModal
          video={activeVideo}
          onClose={() => setActiveVideo(null)}
        />
      ) : null}
    </section>
  );
};

export default VideoCarousel;
